import type { RouteLocationRaw } from 'vue-router';

import Common from '@/libs/common';
import { isHttpError } from '@/libs/api-helper';
import { AppRoute } from '@/libs/routing';
import { trackPushOpen } from '@/libs/matomo-events';
import type { Group } from '@/@types/group';

/**
 * Build the route a tapped push notification must open.
 *
 * `router.push` throws `Missing required param "groupId"` when the group is
 * unresolved, and the router swallows it, so the tap does nothing at all. That
 * happens to a passenger who left a network without picking a new one: the
 * stored group is gone while the device is still subscribed to that network's
 * topic. Nothing local can name the emitter for them, so the honest outcome is
 * the group selection, not a dead tap.
 *
 * The stored group is read as a fallback for the narrower case where the route
 * has not resolved yet and the store is between `setGroup(null)` and the end of
 * `connectGroup`. Notifications carrying a `group_id` skip this and use
 * `buildAlertRoute`.
 */
export function resolvePushNotificationRoute(currentGroupId?: string): RouteLocationRaw {
  const storedGroup: Group | null = Common.safeLSParse('group');
  const groupId = currentGroupId || storedGroup?.group_id;

  if (!groupId) return { name: AppRoute.SELECT_GROUP };

  return { name: AppRoute.PASSENGER_MESSAGE, params: { groupId } };
}

export interface AlertTarget {
  groupId: string;
  alertId?: string;
}

function nonEmptyString(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() !== '' ? value : undefined;
}

/**
 * Read the FCM `data` of an alert push (`group_id`, `alert_id`, `scope`). Entry
 * point shared with any payload carrying the same fields, e.g. a future
 * `appUrlOpen` handler. `scope` only has the `network` value today and does
 * not change the target yet.
 */
export function parseAlertTarget(data: unknown): AlertTarget | null {
  if (typeof data !== 'object' || data === null || !('group_id' in data)) return null;

  const payload = data as Record<string, unknown>;
  const groupId = nonEmptyString(payload.group_id);
  if (!groupId) {
    console.error('Push notification ignored: invalid group_id in data', payload.group_id);
    return null;
  }
  return { groupId, alertId: nonEmptyString(payload.alert_id) };
}

export function buildAlertRoute({ groupId, alertId }: AlertTarget): RouteLocationRaw {
  return { name: AppRoute.PASSENGER_MESSAGE, params: alertId ? { groupId, alertId } : { groupId } };
}

export interface PushNavigationDeps {
  currentGroupId?: string;
  isKnownGroup: (groupId: string) => Promise<boolean>;
  push: (route: RouteLocationRaw) => void;
}

/**
 * Offline is not "unknown": only an HTTP error rejects the network, since
 * `connectGroup` retries until the connection comes back.
 */
export async function isKnownGroup(groupId: string, getGroup: (groupId: string) => Promise<unknown>) {
  if (!navigator.onLine) return true;
  try {
    await getGroup(groupId);
  } catch (error) {
    if (!isHttpError(error)) return true;
    console.error('Push notification network not found:', groupId, error);
    return false;
  }
  return true;
}

/**
 * Open the network and alert named by a tapped push `data`. An invalid or
 * unknown network falls back to the behavior of a notification without data.
 * The route change itself makes `App.vue` connect the network.
 */
export async function openPushNotification(data: unknown, deps: PushNavigationDeps): Promise<void> {
  const target = parseAlertTarget(data);
  trackPushOpen(target?.groupId ?? deps.currentGroupId, target?.alertId);
  const isSwitching = !!target && target.groupId !== deps.currentGroupId;
  if (!target || (isSwitching && !(await deps.isKnownGroup(target.groupId)))) {
    deps.push(resolvePushNotificationRoute(deps.currentGroupId));
    return;
  }
  deps.push(buildAlertRoute(target));
}
